import { createAsyncThunk } from "@reduxjs/toolkit"
import { apiService } from "../../features/shared/apiService"
import { practicesSlice } from "./model"
import { PracticeInviteWithContext } from "../Practice/schema"
import { PracticeRoleEnum } from "../Account/userSlice"
import { DataStatus } from "../enums"
import appInsightsLogger from "../../features/logging/appInsightsLogger"

interface GetAllPartialPracticesArgs {
  practiceIds: string[]
}
export const getAllPartialPractices = createAsyncThunk(
  "practices/getAllPartialPractices",
  async (args: GetAllPartialPracticesArgs, { dispatch }) => {
    const { practiceIds } = args
    try {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practicesDataStatus: DataStatus.SENDING,
        }),
      )
      const partialPractices =
        await apiService.practice.get.PARTIAL_PRACTICES_BY_IDS(practiceIds)
      dispatch(practicesSlice.actions.setPractices(partialPractices))
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practicesDataStatus: DataStatus.LOADED,
        }),
      )
      return partialPractices
    } catch (error) {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practicesDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface GetAllDetachedProvidersArgs {
  providerIds: string[]
}
export const getAllDetachedProviders = createAsyncThunk(
  "practices/getAllDetachedProviders",
  async (args: GetAllDetachedProvidersArgs, { dispatch }) => {
    const { providerIds } = args
    try {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          detachedProvidersDataStatus: DataStatus.SENDING,
        }),
      )
      const providers =
        await apiService.provider.get.PUBLIC_PROVIDERS_BY_ID(providerIds)
      const detachedProviders = providers.filter((provider) => !provider.userId)
      dispatch(practicesSlice.actions.setDetachedProviders(detachedProviders))
      dispatch(
        practicesSlice.actions.updateDataStatus({
          detachedProvidersDataStatus: DataStatus.LOADED,
        }),
      )
      return detachedProviders
    } catch (error) {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          detachedProvidersDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface GetPracticeInvitesForUserArgs {
  userId: string
}
export const getPracticeInvitesForUser = createAsyncThunk(
  "practices/getAllPracticeInvitesForUser",
  async (args: GetPracticeInvitesForUserArgs, { dispatch }) => {
    const { userId } = args
    try {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practiceInvitesDataStatus: DataStatus.SENDING,
        }),
      )
      // v2: invites live on the parent PracticeAffiliate. Fetch affiliates-by-user, then
      // flatten each affiliate's practiceInvites with practiceId/providerId stitched on
      // from the parent affiliate.
      const affiliates =
        await apiService.practice.get.PRACTICE_AFFILIATES_BY_USER_ID({ userId })

      const invitesWithParentContext = affiliates.flatMap((affiliate) =>
        (affiliate.practiceInvites ?? []).map((invite) => ({
          invite,
          practiceId: affiliate.practiceId,
          providerId: affiliate.providerId,
        })),
      )

      const stitchedPracticeInvites: PracticeInviteWithContext[] =
        await Promise.all(
          invitesWithParentContext.map(
            async ({ invite, practiceId, providerId }) => {
              const publicPractice =
                await apiService.practice.get.PUBLIC_PRACTICE_BY_ID({
                  practiceId,
                })

              return {
                ...invite,
                practiceId,
                providerId,
                createdBy: invite.createdBy ?? "",
                practiceStatus: publicPractice.status,
                isInvitationForProviderRole:
                  invite.inviteRoleDisplayIds.includes(
                    PracticeRoleEnum.PROVIDER,
                  ),
                practiceHasConditionalReqs:
                  publicPractice.locations.length > 1 ||
                  !invite.practiceProvidesLiabilityInsurance,
              }
            },
          ),
        )

      dispatch(
        practicesSlice.actions.setPracticeInvites(stitchedPracticeInvites),
      )
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practiceInvitesDataStatus: DataStatus.LOADED,
        }),
      )
      return stitchedPracticeInvites
    } catch (error) {
      dispatch(
        practicesSlice.actions.updateDataStatus({
          practiceInvitesDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)
